import { motion } from 'motion/react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { feedback } from './feedback.js'
import { pressable, withMotion } from './motion.js'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

/** `motion` owns these handlers on a motion element, so the DOM versions are dropped. */
type NativeButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onAnimationStart' | 'onAnimationEnd' | 'onDrag' | 'onDragStart' | 'onDragEnd'
>

export type ButtonProps = NativeButtonProps & {
  variant?: ButtonVariant
  block?: boolean
  children: ReactNode
}

/**
 * Sizing comes from `--tap-min`, which `data-mode` on <html> sets to 64 px in Mini and 48 px
 * in Core (CONVENTIONS §8). Every button needs a Turkish accessible name; the label is it.
 *
 * POLISH §1.1 (POL-01): the press scales to 0.96 and springs back, with a tap sound and a
 * light haptic on native. Reduced motion keeps the sound and drops the travel.
 */
export function Button({
  variant = 'primary',
  block,
  className,
  children,
  onPointerDown,
  ...rest
}: ButtonProps) {
  const classes = ['btn', `btn--${variant}`, block ? 'btn--block' : '', className ?? '']
  return (
    <motion.button
      type="button"
      className={classes.filter(Boolean).join(' ')}
      variants={withMotion(pressable)}
      initial="rest"
      animate="rest"
      whileTap={rest.disabled ? 'rest' : 'pressed'}
      onPointerDown={(event) => {
        if (!rest.disabled) feedback('tap')
        onPointerDown?.(event)
      }}
      {...rest}
    >
      {children}
    </motion.button>
  )
}
